import { Drawer } from "@base-ui/react/drawer";
import { "ui-overlay.close" as closeLabel } from "@repo/i18n";
import { type ReactNode, type TouchEvent, useCallback, useRef } from "react";
import { cx } from "../../lib/cx";
import { impact } from "../../native";
import { CloseGlyph, ICON_BUTTON, SCRIM } from "./parts";
import { LayerOwned, rootMarks, useLayerPresence } from "./presence";

export type SheetHandle = "header" | "whole";

/** A2's `EditSheet`: a drag past this from a handle dismisses at any speed. */
const DISMISS_DISTANCE_PX = 120;

/**
 * Reads a flag another handler may have set since. Through a function because
 * Biome's `noUnnecessaryConditions` narrows `ref.current` to the value it was
 * assigned earlier in the same function and cannot see the other writer.
 */
function isSet(flag: { current: boolean }): boolean {
  return flag.current;
}

export interface SheetProps {
  children: ReactNode;
  /** Merged onto the popup with `cx`. */
  className?: string;
  /**
   * `true` makes the sheet an EPHEMERAL layer instead of a URL one (D02): it
   * is bound to local state, its popup carries `data-ephemeral` (Base UI adds
   * `data-open` itself), so Android Back and Escape close it first — the
   * bridge in `app/back-button.ts` and the trainer's rule in
   * `features/coach/escape-rule.ts` both read that pair. It also lifts the
   * backdrop and the viewport to `z-[70]`, the `Popover`/`Menu` layer, so it
   * opens ABOVE the trainer's `z-52` popup rather than behind it. Default
   * `false`: a plain `Sheet` stays a URL layer at `z-50`.
   */
  ephemeral?: boolean;
  /** A small line above the title (A2's `EditSheet` eyebrow). */
  eyebrow?: ReactNode;
  /**
   * Always as tall as a sheet may be, whatever the body holds. For a sheet
   * whose body CHANGES under the same header — tabs, a filtered list — so
   * an empty tab or a short result does not drop the header and the tabs
   * down the screen, and the next tap lands where the last one did.
   * Default `false`: a sheet fits its content.
   */
  fill?: boolean;
  /** Pinned under the body; lifted above the soft keyboard. */
  footer?: ReactNode;
  /**
   * Where a drag may start. `"header"` (the default) is A2's `EditSheet`: the
   * body scrolls and only the header drags, so a form can scroll. `"whole"`
   * lets the body drag too, once it is scrolled to the top.
   */
  handle?: SheetHandle;
  /**
   * `false` while the coach is docked on desktop (D04): no focus trap, no
   * scroll lock, no scrim, the rest of the page stays interactive, and an
   * outside press does not dismiss — the two coexist, as in A2.
   */
  modal?: boolean;
  /**
   * Bind `false` to the router's `closeLayer()` for a URL sheet (D02, T10), or
   * to local state when `ephemeral`. Called for Escape, the close button, an
   * outside press (modal only) and a swipe down.
   */
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** Base UI Drawer snap points: 0–1 is a fraction of the viewport, >1 pixels, or `"148px"`/`"30rem"`. */
  snapPoints?: (number | string)[];
  /** The sheet's heading, and its accessible name. */
  title: ReactNode;
}

/**
 * The bottom sheet, over Base UI Drawer. Controlled, and ignorant of the
 * router: the feature binds `open` to its URL param — or to local state, with
 * `ephemeral`, which is also what lets it open above the trainer.
 *
 * Swipe down dismisses: Base UI's rule (past half the sheet's height, or a
 * flick) plus A2's (a drag of more than 120 px from a handle, at any speed —
 * without it a tall sheet needs a 400 px drag). With `snapPoints` only Base
 * UI's rule applies, since a long drag means "snap lower", not "close".
 * On ≥1024 px it is a centred panel, kept left of `--coach-dock`.
 * Portalled under `<body>`, it takes its theme from `<html>` (D03).
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  eyebrow,
  snapPoints,
  modal: modalProp,
  handle,
  ephemeral,
  children,
  footer,
  className,
  fill,
}: SheetProps) {
  // Base UI skips a swipe that starts inside an element carrying this
  // attribute; on the body and footer it leaves the header as the only handle.
  const modal = modalProp ?? true;
  const ignoreSwipe =
    handle === "whole" ? {} : { "data-base-ui-swipe-ignore": "" };
  const keyboardInset = "var(--drawer-keyboard-inset,0px)";
  // `Popover`/`Menu`'s layer when ephemeral, so the sheet clears the trainer's
  // `z-52` popup; the URL sheet keeps `z-50`, below it.
  const layer = ephemeral ? "z-[70]" : "z-50";
  const ephemeralMark = ephemeral ? { "data-ephemeral": "" } : {};

  // `D193`: a URL sheet stays mounted while it leaves. The shell flips
  // `present` and the sheet closes through Base UI like any other close; the
  // root sheet of a layer — never an ephemeral one, never one nested in
  // another layer — reports the exit's end so the shell can unmount it.
  const presence = useLayerPresence();
  const owner = !(ephemeral || presence.owned) && presence.slot !== null;
  const shown = open && presence.present;
  const { done } = presence;
  const handleOpenChangeComplete = useCallback(
    (next: boolean) => {
      if (!next && owner) {
        done();
      }
    },
    [done, owner]
  );
  const layerMark = owner
    ? rootMarks(presence.slot, presence.present, presence.covered)
    : {};

  // A2's distance rule, beside Base UI's. `closeRequested` is how a touchend
  // learns whether Base UI already closed on the same gesture, so the layer
  // is never closed twice (twice would be two `history.back()`s).
  const dragStartY = useRef<number | null>(null);
  const closeRequested = useRef<boolean>(false);
  // The swipe crossed the distance that dismisses: one light tick, once per
  // drag, the moment letting go would close (`D193`).
  const pastThreshold = useRef<boolean>(false);
  const withSnapPoints = snapPoints !== undefined;
  const wholeDrags = handle === "whole";

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        closeRequested.current = true;
      }
      onOpenChange(next);
    },
    [onOpenChange]
  );

  const handleTouchMove = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const startY = dragStartY.current;
    if (startY === null || event.touches.length !== 1) {
      return;
    }
    const past = event.touches.item(0).clientY - startY > DISMISS_DISTANCE_PX;
    if (past && !isSet(pastThreshold)) {
      impact("light").catch(() => undefined);
    }
    pastThreshold.current = past;
  }, []);

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      closeRequested.current = false;
      pastThreshold.current = false;
      dragStartY.current = null;
      const { target, touches } = event;
      const touch = touches.item(0);
      if (withSnapPoints || touches.length !== 1) {
        return;
      }
      if (!(target instanceof Element)) {
        return;
      }
      const fromHandle =
        target.closest("[data-sheet-handle]") !== null &&
        target.closest("button") === null;
      const body = target.closest("[data-sheet-body]");
      const fromBodyTop = wholeDrags && body !== null && body.scrollTop <= 0;
      if (fromHandle || fromBodyTop) {
        dragStartY.current = touch.clientY;
      }
    },
    [withSnapPoints, wholeDrags]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      const startY = dragStartY.current;
      dragStartY.current = null;
      if (startY === null || event.changedTouches.length === 0) {
        return;
      }
      const touch = event.changedTouches.item(0);
      if (touch.clientY - startY <= DISMISS_DISTANCE_PX) {
        return;
      }
      // Base UI settles the same touchend in a handler on an ANCESTOR (the
      // viewport), which runs after this one: look once it has had its turn.
      setTimeout(() => {
        if (!isSet(closeRequested)) {
          handleOpenChange(false);
        }
      }, 0);
    },
    [handleOpenChange]
  );

  const handleTouchCancel = useCallback(() => {
    dragStartY.current = null;
  }, []);

  return (
    <Drawer.Root
      disablePointerDismissal={!modal}
      modal={modal}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      open={shown}
      snapPoints={snapPoints}
      swipeDirection="down"
    >
      <Drawer.VirtualKeyboardProvider>
        <Drawer.Portal>
          {modal ? (
            <Drawer.Backdrop
              className={cx(
                SCRIM,
                layer,
                "opacity-[calc(1-var(--drawer-swipe-progress,0))] data-swiping:duration-0"
              )}
            />
          ) : null}
          <Drawer.Viewport
            className={cx(
              "fixed inset-0 flex items-end justify-center lg:right-[var(--coach-dock,0px)] lg:items-center lg:p-6",
              layer,
              !modal && "pointer-events-none"
            )}
            data-sheet-viewport=""
          >
            <Drawer.Popup
              className={cx(
                "pointer-events-auto relative flex max-h-[92dvh] w-full max-w-[560px] flex-col overflow-hidden rounded-t-3xl bg-canvas text-ink shadow-lift outline-none",
                "[transform:translateY(calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y,0px)))]",
                "transition-transform duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)] data-swiping:duration-0",
                "data-ending-style:[transform:translateY(100%)] data-starting-style:[transform:translateY(100%)]",
                "data-ending-style:duration-[calc(var(--drawer-swipe-strength,1)*300ms)]",
                "lg:max-h-[86dvh] lg:rounded-3xl",
                fill && "h-[92dvh] lg:h-[86dvh]",
                className
              )}
              data-surface="sheet"
              onTouchCancel={handleTouchCancel}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
              onTouchStart={handleTouchStart}
              {...ephemeralMark}
              {...layerMark}
            >
              <div
                className="shrink-0 cursor-grab touch-none select-none"
                data-sheet-handle=""
              >
                <div className="flex justify-center pt-2.5 pb-1 lg:hidden">
                  <span className="h-1.5 w-10 rounded-full bg-line-strong" />
                </div>
                <div className="flex items-start gap-3 px-5 pt-2 lg:px-7 lg:pt-7">
                  <div className="min-w-0 flex-1 pt-1">
                    {eyebrow ? (
                      <div className="font-semibold text-ink-3 text-sm">
                        {eyebrow}
                      </div>
                    ) : null}
                    <Drawer.Title className="a-display mt-0.5 text-balance text-2xl">
                      {title}
                    </Drawer.Title>
                  </div>
                  <Drawer.Close
                    aria-label={closeLabel()}
                    className={ICON_BUTTON}
                  >
                    <CloseGlyph />
                  </Drawer.Close>
                </div>
              </div>
              <Drawer.Content
                className="a-scroll min-h-0 flex-1 touch-auto px-5 pt-5 lg:px-7"
                data-sheet-body=""
                style={{
                  paddingBottom: footer
                    ? "24px"
                    : `calc(max(24px, env(safe-area-inset-bottom)) + ${keyboardInset})`,
                }}
                {...ignoreSwipe}
              >
                <LayerOwned>{children}</LayerOwned>
              </Drawer.Content>
              {footer ? (
                <div
                  className="shrink-0 border-line border-t px-5 pt-3 lg:px-7"
                  style={{
                    paddingBottom: `calc(max(16px, env(safe-area-inset-bottom)) + ${keyboardInset})`,
                  }}
                  {...ignoreSwipe}
                >
                  <LayerOwned>{footer}</LayerOwned>
                </div>
              ) : null}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.VirtualKeyboardProvider>
    </Drawer.Root>
  );
}
